/*
 * deshio-image-perf.js
 * --------------------------------------------------------------
 * Fixes two measured problems with the theme's image pipeline.
 *
 * 1) SCROLL JANK
 *    theme.min.js `BlsLazyloadImg` runs an IntersectionObserver that,
 *    for every image entering view, does:
 *        img.width  = entry.boundingClientRect.width
 *        img.height = entry.boundingClientRect.height
 *        img.sizes  = `${entry.boundingClientRect.width}px`
 *    Writing width/height/sizes right after reading getBoundingClientRect
 *    forces a synchronous layout per image, during scroll. Worse,
 *    `BlsLazyloadImg.init()` is called ~39 times across the theme and
 *    each call builds a NEW observer over every `.bls-image-js img`
 *    without disconnecting the previous one, so the same image is
 *    observed many times over and each firing repeats that layout.
 *
 * 2) IMAGES APPEARING LATE
 *    critical.min.css has:
 *        html.js .bls-loading-image img { content: url(swatch_loading.png) }
 *    so an image stays visually replaced by a placeholder until the
 *    observer removes `.bls-loading-image` from its wrapper — a fully
 *    downloaded image can still be hidden. A 1s fadeIn then runs on top.
 *
 * Approach: reveal images from their own load/decode state instead of
 * from scroll position, and stop the observers from thrashing layout.
 * We do not edit theme.min.js (vendor file, overwritten on theme update).
 *
 * ponytail: patches the vendor lazyloader from outside rather than
 * forking theme.min.js. If Blueskytechco ever fixes BlsLazyloadImg,
 * delete this file — nothing else depends on it.
 */
(function () {
  'use strict';

  var REVEAL = 'bls-loaded-image';
  var LOADING = 'bls-loading-image';

  /* Reveal one image: drop the wrapper's loading class so the CSS
     placeholder stops overriding the real pixels. */
  function reveal(img) {
    if (!img || img.dataset.deshioRevealed === '1') return;
    img.dataset.deshioRevealed = '1';
    img.classList.add(REVEAL);
    var wrap = img.closest('.bls-image-js');
    if (wrap) wrap.classList.remove(LOADING);
  }

  /* An image is "ready" once the browser has pixels for it. complete
     covers cache hits, which is the common case on reload and exactly
     where the old code still waited for a scroll event. */
  function settle(img) {
    if (img.complete && img.naturalWidth > 0) {
      reveal(img);
      return;
    }
    img.addEventListener('load', function () { reveal(img); }, { once: true });
    // A broken image must not stay under a shimmering placeholder forever.
    img.addEventListener('error', function () { reveal(img); }, { once: true });
  }

  function sweep(root) {
    var imgs = (root || document).querySelectorAll('.bls-image-js img');
    for (var i = 0; i < imgs.length; i++) settle(imgs[i]);
  }

  /* Neutralise the vendor lazyloader. We replace its init with our own
      sweep so the ~39 call sites across the theme stop creating stacked
      observers that force layout on every scroll tick. */
  function patchVendor() {
    if (typeof window.BlsLazyloadImg !== 'object' || !window.BlsLazyloadImg) return false;
    if (window.BlsLazyloadImg.__deshioPatched) return true;
    window.BlsLazyloadImg.__deshioPatched = true;
    window.BlsLazyloadImg.lazyReady = function () { sweep(document); };
    window.BlsLazyloadImg.init = function () { sweep(document); };
    return true;
  }

  function start() {
    patchVendor();
    sweep(document);

    /* Sections re-render on sort/filter/load-more and inject fresh
       markup. One observer on the tree is far cheaper than the vendor's
       per-image observers, and it never reads layout. */
    if (window.MutationObserver) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          sweep(document);
        });
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* theme.min.js is deferred and may define BlsLazyloadImg after us;
     re-patch once the load event has certainly run it. */
  window.addEventListener('load', function () {
    patchVendor();
    sweep(document);
  }, { once: true });
})();
